import React, { useState, useEffect } from 'react';
import {
  Window,
  WindowHeader,
  WindowContent,
  Button,
  TextInput,
  Fieldset,
  Toolbar,
  Separator,
  GroupBox,
  Select
} from 'react95';
import { useClients, useDebtAccounts } from '../hooks';
import { DEBT_CODES, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../models/constants';
import { validateClient, createClient, searchClients } from '../models/Client';
import { formatCurrency } from '../models/DebtAccount';

/**
 * Enhanced Client Details Form Component
 * 
 * Features implemented according to task 4 requirements:
 * - Comprehensive client form with all required fields
 * - Debt code dropdown with available options
 * - Form validation and error display
 * - Integration with financial summary display
 * - Save, clear, and navigation functionality
 * 
 * Requirements: 1.1, 1.2, 1.5, 1.6
 */
const ClientDetailsForm = ({ 
  initialClient = null, 
  onClientSaved = () => {}, 
  onClientSelected = () => {},
  showFinancialSummary = true 
}) => {
  const clients = useClients();
  const debtAccounts = useDebtAccounts();
  
  // Form state
  const [formData, setFormData] = useState(() => 
    initialClient ? { ...initialClient } : createClient()
  );
  
  // UI state
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  
  // Financial data for selected client
  const [clientDebtAccounts, setClientDebtAccounts] = useState([]);
  const [financialSummary, setFinancialSummary] = useState({
    totalDebt: 0,
    totalPayments: 0,
    currentBalance: 0,
    lastPaymentDate: null,
    lastPaymentAmount: 0
  });

  // Load debt accounts when client changes
  useEffect(() => {
    if (formData.id) {
      const accounts = debtAccounts.getDebtAccountsByClientId(formData.id);
      setClientDebtAccounts(accounts);
      
      // Calculate financial summary
      const summary = accounts.reduce((acc, account) => ({
        totalDebt: acc.totalDebt + account.debtAmount + account.adjustments + account.legalFees + account.interest + account.debtFees,
        totalPayments: acc.totalPayments + account.payments,
        currentBalance: acc.currentBalance + account.balance,
        lastPaymentDate: account.lastPaymentDate && (!acc.lastPaymentDate || account.lastPaymentDate > acc.lastPaymentDate) 
          ? account.lastPaymentDate 
          : acc.lastPaymentDate,
        lastPaymentAmount: account.lastPaymentDate && (!acc.lastPaymentDate || account.lastPaymentDate > acc.lastPaymentDate)
          ? account.lastPaymentAmount
          : acc.lastPaymentAmount
      }), {
        totalDebt: 0,
        totalPayments: 0,
        currentBalance: 0,
        lastPaymentDate: null,
        lastPaymentAmount: 0
      });
      
      setFinancialSummary(summary);
    } else {
      setClientDebtAccounts([]);
      setFinancialSummary({
        totalDebt: 0,
        totalPayments: 0,
        currentBalance: 0,
        lastPaymentDate: null,
        lastPaymentAmount: 0
      });
    }
  }, [formData.id, debtAccounts.items]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    // Safely extract the value - React95 Select might pass an object
    let actualValue = value;
    if (value && typeof value === 'object' && value.value !== undefined) {
      actualValue = value.value;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: actualValue,
      updatedAt: new Date()
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
    
    // Clear success message
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  // Handle client search
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    
    if (searchValue.trim().length > 0) {
      const results = searchClients(clients.items, searchValue);
      setSearchResults(results);
      setShowSearch(true);
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
  };

  // Handle client selection from search
  const handleSelectClient = (client) => {
    setFormData({ ...client });
    setSearchTerm('');
    setShowSearch(false);
    setSearchResults([]);
    onClientSelected(client);
  };

  // Validate form data
  const validateForm = () => {
    // Debug logging to help identify issues
    console.log('Validating form data:', formData);
    console.log('Form data types:', {
      clientName: typeof formData.clientName,
      debtCode: typeof formData.debtCode,
      email: typeof formData.email
    });
    
    const validation = validateClient(formData);
    setErrors(validation.errors);
    return validation.isValid;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      let result;
      
      if (formData.id && clients.getById(formData.id)) {
        // Update existing client
        result = await clients.update(formData.id, formData);
      } else {
        // Create new client
        const newClient = createClient(formData);
        result = await clients.create(newClient);
        setFormData(result.data);
      }
      
      if (result.success) {
        setSuccessMessage(SUCCESS_MESSAGES.CLIENT_SAVED);
        onClientSaved(result.data);
      } else {
        setErrors(result.errors || [ERROR_MESSAGES.SAVE_ERROR]);
      }
    } catch (error) {
      console.error('Error saving client:', error);
      setErrors([ERROR_MESSAGES.SAVE_ERROR]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clear
  const handleClear = () => {
    const newClient = createClient();
    setFormData(newClient);
    setErrors([]);
    setSuccessMessage('');
    setSearchTerm('');
    setShowSearch(false);
    setSearchResults([]);
  };

  // Handle new client
  const handleNew = () => {
    handleClear();
  };

  return (
    <Window style={{ width: '900px', minHeight: '700px' }}>
      <WindowHeader>
        <span>👤 Client Details - {formData.clientName || 'New Client'}</span>
      </WindowHeader>
      
      {/* Toolbar */}
      <Toolbar style={{ justifyContent: 'flex-start' }}>
        <Button size="sm" onClick={handleSave} disabled={isLoading}>
          💾 Save
        </Button>
        <Button size="sm" onClick={handleClear}>
          🗑️ Clear
        </Button>
        <Separator orientation="vertical" />
        <Button size="sm" onClick={handleNew}>
          📄 New
        </Button>
        <Button size="sm" onClick={() => setShowSearch(!showSearch)}>
          🔍 Search
        </Button>
        <Separator orientation="vertical" />
        <Button size="sm">
          🖨️ Print
        </Button>
        <Button size="sm">
          📧 Email
        </Button>
      </Toolbar>

      <WindowContent style={{ padding: '10px' }}>
        
        {/* Search Section */}
        {showSearch && (
          <Fieldset label="Search Clients" style={{ marginBottom: '10px' }}>
            <div style={{ padding: '10px' }}>
              <TextInput
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by name or debt code..."
                style={{ width: '300px', marginBottom: '10px' }}
              />
              
              {searchResults.length > 0 && (
                <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px inset #c0c0c0', backgroundColor: 'white' }}>
                  {searchResults.map(client => (
                    <div
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      style={{
                        padding: '5px 10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e0e0e0',
                        ':hover': { backgroundColor: '#f0f0f0' }
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <strong>{client.clientName}</strong> - {client.debtCode}
                      {client.phone && <span style={{ color: '#666', marginLeft: '10px' }}>📞 {client.phone}</span>}
                    </div>
                  ))}
                </div>
              )}
              
              {searchTerm && searchResults.length === 0 && (
                <div style={{ color: '#666', fontStyle: 'italic' }}>
                  No clients found matching "{searchTerm}"
                </div>
              )}
            </div>
          </Fieldset>
        )}

        {/* Error Display */}
        {errors.length > 0 && (
          <div style={{ 
            marginBottom: '10px', 
            padding: '10px', 
            backgroundColor: '#ffe0e0', 
            border: '1px solid #ff0000',
            color: '#cc0000'
          }}>
            <strong>⚠️ Please correct the following errors:</strong>
            <ul style={{ margin: '5px 0 0 20px' }}>
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div style={{ 
            marginBottom: '10px', 
            padding: '10px', 
            backgroundColor: '#e0ffe0', 
            border: '1px solid #00aa00',
            color: '#006600'
          }}>
            ✅ {successMessage}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', height: '100%' }}>
          
          {/* Main Form Area */}
          <div style={{ flex: 2 }}>
            <GroupBox label="Client Information">
              <div style={{ padding: '10px' }}>
                
                {/* Client Name and Debt Code Row */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Client Name: *
                    </label>
                    <TextInput
                      value={formData.clientName}
                      onChange={(e) => handleInputChange('clientName', e.target.value)}
                      placeholder="Enter client name"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div style={{ width: '150px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Debt Code: *
                    </label>
                    <Select
                      value={formData.debtCode}
                      onChange={(value) => handleInputChange('debtCode', value)}
                      options={[
                        { value: '', label: 'Select Debt Code' },
                        ...DEBT_CODES.map(code => ({ value: code, label: code }))
                      ]}
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Address Section */}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    Home Address:
                  </label>
                  <TextInput
                    value={formData.homeAddress}
                    onChange={(e) => handleInputChange('homeAddress', e.target.value)}
                    placeholder="Enter home address"
                    style={{ width: '100%' }}
                    disabled={isLoading}
                  />
                </div>

                {/* Contact Information Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Phone:
                    </label>
                    <TextInput
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Work Phone:
                    </label>
                    <TextInput
                      value={formData.workPhone}
                      onChange={(e) => handleInputChange('workPhone', e.target.value)}
                      placeholder="(555) 123-4567"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Cell Phone:
                    </label>
                    <TextInput
                      value={formData.cell}
                      onChange={(e) => handleInputChange('cell', e.target.value)}
                      placeholder="(555) 123-4567"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Fax:
                    </label>
                    <TextInput
                      value={formData.fax}
                      onChange={(e) => handleInputChange('fax', e.target.value)}
                      placeholder="(555) 123-4567"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Email and Contact Row */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Email:
                    </label>
                    <TextInput
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="client@example.com"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Contact Person:
                    </label>
                    <TextInput
                      value={formData.contact}
                      onChange={(e) => handleInputChange('contact', e.target.value)}
                      placeholder="Contact person name"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* License, SSN, and Account Rep Row */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Driver's License:
                    </label>
                    <TextInput
                      value={formData.drLic}
                      onChange={(e) => handleInputChange('drLic', e.target.value)}
                      placeholder="License number"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      SSN:
                    </label>
                    <TextInput
                      value={formData.ssn}
                      onChange={(e) => handleInputChange('ssn', e.target.value)}
                      placeholder="XXX-XX-XXXX"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                      Account Rep:
                    </label>
                    <TextInput
                      value={formData.acctRep}
                      onChange={(e) => handleInputChange('acctRep', e.target.value)}
                      placeholder="Account representative"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Alias Field */}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    Alias:
                  </label>
                  <TextInput
                    value={formData.alias}
                    onChange={(e) => handleInputChange('alias', e.target.value)}
                    placeholder="Alternative name or alias"
                    style={{ width: '100%' }}
                    disabled={isLoading}
                  />
                </div>

                {/* Comment Field */}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '11px', fontWeight: 'bold' }}>
                    Comments:
                  </label>
                  <textarea
                    value={formData.comment}
                    onChange={(e) => handleInputChange('comment', e.target.value)}
                    placeholder="Additional comments or notes"
                    style={{
                      width: '100%',
                      height: '80px',
                      fontFamily: 'MS Sans Serif',
                      fontSize: '11px',
                      border: '2px inset #c0c0c0',
                      padding: '4px',
                      resize: 'vertical'
                    }}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </GroupBox>
          </div>

          {/* Financial Summary Sidebar */}
          {showFinancialSummary && (
            <div style={{ width: '250px' }}>
              <GroupBox label="Financial Summary" style={{ marginBottom: '10px' }}>
                <div style={{ padding: '10px', fontSize: '11px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Total Debt:</strong><br/>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#cc0000' }}>
                      {formatCurrency(financialSummary.totalDebt)}
                    </span>
                  </div>
                  
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Total Payments:</strong><br/>
                    <span style={{ fontSize: '12px', color: '#006600' }}>
                      {formatCurrency(financialSummary.totalPayments)}
                    </span>
                  </div>
                  
                  <div style={{ marginBottom: '8px', paddingTop: '5px', borderTop: '1px solid #808080' }}>
                    <strong>Current Balance:</strong><br/>
                    <span style={{ 
                      fontSize: '14px', 
                      fontWeight: 'bold', 
                      color: financialSummary.currentBalance > 0 ? '#cc0000' : '#006600' 
                    }}>
                      {formatCurrency(financialSummary.currentBalance)}
                    </span>
                  </div>
                  
                  {financialSummary.lastPaymentDate && (
                    <div style={{ paddingTop: '8px', borderTop: '1px solid #808080' }}>
                      <strong>Last Payment:</strong><br/>
                      <div style={{ fontSize: '10px', color: '#666' }}>
                        {new Date(financialSummary.lastPaymentDate).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 'bold' }}>
                        {formatCurrency(financialSummary.lastPaymentAmount)}
                      </div>
                    </div>
                  )}
                  
                  {clientDebtAccounts.length === 0 && formData.id && (
                    <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', marginTop: '10px' }}>
                      No debt accounts found
                    </div>
                  )}
                </div>
              </GroupBox>

              {/* Debt Accounts List */}
              {clientDebtAccounts.length > 0 && (
                <GroupBox label={`Debt Accounts (${clientDebtAccounts.length})`}>
                  <div style={{ padding: '10px', fontSize: '10px', maxHeight: '200px', overflow: 'auto' }}>
                    {clientDebtAccounts.map((account, index) => (
                      <div key={account.id} style={{ 
                        marginBottom: '8px', 
                        padding: '5px', 
                        backgroundColor: '#f8f8f8',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ fontWeight: 'bold' }}>Account #{index + 1}</div>
                        <div>Debt: {formatCurrency(account.debtAmount)}</div>
                        <div>Balance: {formatCurrency(account.balance)}</div>
                        {account.lastPaymentDate && (
                          <div style={{ color: '#666' }}>
                            Last Payment: {new Date(account.lastPaymentDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </GroupBox>
              )}
            </div>
          )}
        </div>

        {/* Status Bar */}
        <div style={{ 
          marginTop: '10px', 
          padding: '5px 10px', 
          backgroundColor: '#c0c0c0', 
          border: '1px inset #c0c0c0',
          fontSize: '11px',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <span>
            {formData.id ? `Client ID: ${formData.id}` : 'New Client'}
          </span>
          <span>
            {formData.createdAt && `Created: ${new Date(formData.createdAt).toLocaleDateString()}`}
            {formData.updatedAt && formData.createdAt !== formData.updatedAt && 
              ` | Updated: ${new Date(formData.updatedAt).toLocaleDateString()}`
            }
          </span>
          <span>
            Total Clients: {clients.items.length}
          </span>
        </div>
      </WindowContent>
    </Window>
  );
};

export default ClientDetailsForm;